import { AvatarGroup, Badge, Box, HStack, Icon, LinkBox, Text, VStack } from '@chakra-ui/react'
import { UserAvatar } from '@src/components/sco/Account/UserAvatar'
import { ScoThread } from '@src/graphql-types'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { gql } from '@src/lib/gql'
import he from 'he'
import moment from 'moment'
import { GiTeacher } from 'react-icons/gi'
import { LuBotMessageSquare, LuLock, LuMessageSquare, LuMessagesSquare, LuUsers } from 'react-icons/lu'

function stripFormatting(input?: string): string {
  if (!input) {
    return ''
  }
  // 1) Decode HTML entities first so &lt;b&gt; becomes <b>, which we can strip
  let s = he.decode(input)
  // 2) Strip HTML tags
  s = s.replace(/<\/?[^>]+(>|$)/g, '')
  // 3) Markdown: images -> alt text
  s = s.replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
  // 4) Markdown: links -> link text
  s = s.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
  // 5) Remove emphasis/bold/strike/inline-code markers (* _ ~ `)
  s = s.replace(/[*_~`]+/g, '')
  // 6) Remove headings, blockquotes, lists
  s = s.replace(/^\s{0,3}#{1,6}\s+/gm, '')
  s = s.replace(/^\s{0,3}>\s?/gm, '')
  s = s.replace(/^\s{0,3}[-*+]\s+/gm, '')
  s = s.replace(/^\s*\d+\.\s+/gm, '')
  // 7) Collapse whitespace and trim
  s = s.replace(/\s+/g, ' ').trim()
  return s
}

type ChatThreadListItemProps = {
  isActive: boolean
  onSelect?: (thread) => void
  thread: ScoThread
  triggerRefetch?: () => void
  showEmptyThreads?: boolean
}

const icons = {
  commenting: LuMessagesSquare,
  'public-chat': LuMessageSquare,
  'advisor-chat': GiTeacher,
  'private-chat': LuLock,
  'direct-chat': LuUsers,
  'tool-chat': LuBotMessageSquare,
}

export const ChatThreadListItem = (props: ChatThreadListItemProps) => {
  const thread = props.thread
  const { currentUser } = useCurrentUser()
  const showAsPerson =
    thread.threadTypeKey === 'direct-chat' ||
    (thread.threadTypeKey === 'tool-chat' && !!thread.counterpartUserId)

  return (
    <LinkBox
      bgColor={props.isActive ? 'colorPalette.card' : 'transparent'}
      borderBottom={'1px solid'}
      borderBottomColor={'colorPalette.border/25'}
      cursor={'pointer'}
      fontSize={'sm'}
      maxWidth={'100%'}
      padding={4}
      width={'full'}
      _hover={{
        bgColor: props.isActive ? 'colorPalette.container' : 'colorPalette.container/50',
        color: 'white',
      }}
      onClick={() => props.onSelect && props.onSelect(thread)}
    >
      {thread.threadTypeKey === 'tool-chat' && !showAsPerson && (
        <HStack gap={3}>
          <Icon as={icons[thread.threadTypeKey] || LuMessagesSquare} boxSize={'1.8em'} minWidth={'2.25em'} />
          <Box>
            <HStack>
              <Text
                color={props.isActive ? 'colorPalette.fg' : 'colorPalette.fg/80'}
                flexGrow={1}
                fontWeight={'semibold'}
              >
                {thread.toolTitle || thread.toolSession?.tool?.title || 'Tool'}
              </Text>
            </HStack>
          </Box>
        </HStack>
      )}
      {['commenting', 'public-chat', 'private-chat', 'advisor-chat'].includes(thread.threadTypeKey) && (
        <HStack gap={3}>
          <Icon as={icons[thread.threadTypeKey] || LuMessagesSquare} boxSize={'1.8em'} minWidth={'2.25em'} />
          <Box>
            <HStack>
              <Text
                color={props.isActive ? 'colorPalette.fg' : 'colorPalette.fg/80'}
                flexGrow={1}
                fontWeight={'semibold'}
              >
                {thread.title}
              </Text>
            </HStack>
          </Box>
        </HStack>
      )}
      {showAsPerson && (
        <HStack gap={3}>
          <AvatarGroup size={'xs'}>
            {thread.counterpartUserId ? (
              <UserAvatar
                avatarProps={{
                  size: 'md',
                }}
                user={{
                  userId: thread.counterpartUserId,
                  fullName: thread.counterpartFullName,
                  displayName: thread.counterpartDisplayName,
                  pictureUrl: thread.counterpartPictureUrl,
                }}
              />
            ) : (
              thread.userMemberships?.items
                ?.filter(m => thread.userMemberships.items.length === 1 || m.userId !== currentUser?.userId)
                .map((membership, idx) => (
                  <UserAvatar
                    key={idx}
                    user={membership.user}
                    avatarProps={{
                      size: 'md',
                    }}
                  />
                ))
            )}
          </AvatarGroup>

          <VStack align={'stretch'} flexGrow={1} gap={0}>
            <HStack align={'flex-start'}>
              <Text
                color={props.isActive ? 'colorPalette.fg' : 'colorPalette.fg/80'}
                flexGrow={1}
                fontFamily={'Poppins'}
                fontWeight={'semibold'}
                lineClamp={1}
              >
                {thread.counterpartFullName ||
                  thread.counterpartDisplayName ||
                  thread.userMemberships?.items
                    ?.filter(
                      m => thread.userMemberships.items.length === 1 || m.userId !== currentUser?.userId
                    )
                    .map(member => member.user.fullName || member.user.displayName)
                    .join(', ')}
              </Text>
              <Text
                color={'colorPalette.fg/50'}
                fontSize={'10px'}
                textStyle={'labelXs'}
                whiteSpace={'nowrap'}
              >
                {thread.lastActiveAt ? moment(thread.lastActiveAt).fromNow(true) : ''}
              </Text>
            </HStack>
            <HStack>
              {thread.preview || thread.mostRecentComment ? (
                <Text
                  color={'colorPalette.fg/70'}
                  flexGrow={1}
                  fontSize={'xs'}
                  lineClamp={1}
                  wordBreak={'break-word'}
                >
                  {thread.preview || stripFormatting(thread.mostRecentComment?.body).substring(0, 60)}
                </Text>
              ) : (
                <Text flexGrow={1}></Text>
              )}
              {(thread.unreadCommentsCount || thread.currentUserUnreadCommentsCount) > 0 && (
                <Badge boxShadow={'xs'} variant={'count'}>
                  {thread.unreadCommentsCount || thread.currentUserUnreadCommentsCount}
                </Badge>
              )}
            </HStack>
          </VStack>
        </HStack>
      )}
    </LinkBox>
  )
}

ChatThreadListItem.fragments = {
  ScoInboxThread: () => gql`
    fragment _ on ScoInboxThread {
      id
      threadId
      threadTypeKey
      title
      lastActiveAt
      unreadCommentsCount
      preview
      counterpartUserId
      counterpartDisplayName
      counterpartFullName
      counterpartPictureUrl
      toolTitle
    }
  `,
  ScoThread: () => gql`
    fragment _ on ScoThread {
      id
      slug
      threadId
      threadTypeKey
      title
      currentUserUnreadCommentsCount
      lastActiveAt
      toolSession {
        id
        tool {
          id
          title
        }
      }
      mostRecentComment {
        id
        body
        createdAt
        deletedAt
      }
      userMemberships {
        id
        items {
          id
          userId
          user {
            id
            userId
            fullName
            displayName
            firstName
            lastName
            slug
            pictureUrl
            lastActiveAt
            isAdmin
          }
        }
      }
    }
  `,
}
